/**
 * Data import page — upload CAS PDFs, drawdown/distribution notices.
 * Shows extraction job status and allows confirming imported data.
 */
import { useState, useEffect, useCallback, useRef } from 'react'
import { useAuth } from '../Auth/AuthContext'
import { api } from '../../lib/api'

const TYPE_LABELS = {
  cas: 'CAS Statement',
  drawdown: 'Drawdown Notice',
  distribution: 'Distribution Notice',
}

export default function DataImport() {
  const { currentOrg, orgError, refreshOrgs } = useAuth()
  const [uploads, setUploads] = useState([])
  const [fileQueue, setFileQueue] = useState([])
  const [dragOver, setDragOver] = useState(false)
  const [error, setError] = useState(null)
  const fileInputRef = useRef(null)

  const orgId = currentOrg?.id

  const loadUploads = useCallback(async () => {
    if (!orgId) return
    try {
      const data = await api.get(`/uploads/${orgId}`)
      setUploads(data)
      return data
    } catch (e) {
      console.error('Failed to load uploads:', e)
      return []
    }
  }, [orgId])

  useEffect(() => { loadUploads() }, [loadUploads])

  // Poll while any upload is still processing
  useEffect(() => {
    const hasProcessing = uploads.some(u => u.status === 'processing')
    if (!hasProcessing) return
    const timer = setInterval(loadUploads, 3000)
    return () => clearInterval(timer)
  }, [uploads, loadUploads])

  async function handleFiles(files) {
    if (!orgId) {
      setError('Organization data is not loaded yet. Try refreshing the page.')
      return
    }
    setError(null)

    const queue = Array.from(files).map(f => ({ file: f, status: 'uploading', detail: null }))
    setFileQueue(queue)

    for (let i = 0; i < queue.length; i++) {
      try {
        const result = await api.upload(orgId, queue[i].file)
        queue[i] = { ...queue[i], status: 'done', detail: TYPE_LABELS[result.file_type] || result.file_type }
      } catch (e) {
        queue[i] = { ...queue[i], status: 'error', detail: e.message }
      }
      setFileQueue([...queue])
    }

    loadUploads()
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  function handleDrop(e) {
    e.preventDefault()
    setDragOver(false)
    handleFiles(e.dataTransfer.files)
  }

  const uploading = fileQueue.some(f => f.status === 'uploading')

  return (
    <div className="w-full max-w-4xl mx-auto px-1 sm:px-0 py-2 sm:py-4">
      <h2 className="text-xl sm:text-2xl font-bold text-ink mb-4 sm:mb-6">Import Data</h2>

      {orgError && (
        <div className="mb-4 px-3 sm:px-4 py-3 rounded-lg bg-warning/40 border border-warning-border text-warning-fg text-sm flex flex-col sm:flex-row gap-2 sm:gap-0 sm:items-center sm:justify-between">
          <span>Could not load organization data: {orgError}</span>
          <button
            onClick={refreshOrgs}
            className="sm:ml-4 w-full sm:w-auto px-3 py-1 bg-brand hover:opacity-90 text-ink-onAccent rounded text-xs font-medium transition"
          >
            Retry
          </button>
        </div>
      )}

      {error && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-danger/40 border border-danger-border text-danger-fg text-sm">
          {error}
        </div>
      )}

      {/* Drop Zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-xl p-6 sm:p-10 text-center transition ${
          dragOver ? 'border-brand bg-brand/10' : 'border-edge-subtle hover:border-edge'
        }`}
      >
        <div className="text-ink-muted mb-1">
          {uploading ? 'Uploading...' : 'Drop PDF files here or click to browse'}
        </div>
        <div className="text-ink-subtle text-xs mb-3">
          CAS statements, drawdown notices, and distribution notices are detected automatically.
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf"
          multiple
          onChange={(e) => handleFiles(e.target.files)}
          className="hidden"
          id="file-input"
        />
        <label
          htmlFor="file-input"
          className={`inline-block px-6 py-2 rounded-lg text-sm font-medium transition ${
            uploading
              ? 'bg-surface-tertiary text-ink-subtle cursor-wait'
              : 'bg-brand hover:opacity-90 text-ink-onAccent cursor-pointer'
          }`}
        >
          {uploading ? 'Processing...' : 'Select Files'}
        </label>
      </div>

      {/* Current upload batch progress */}
      {fileQueue.length > 0 && (
        <div className="mt-4 space-y-2">
          {fileQueue.map((item, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 bg-surface-secondary border border-edge-subtle rounded-lg text-sm">
              <span className="text-ink flex-1 min-w-0 truncate">{item.file.name}</span>
              {item.status === 'uploading' && (
                <span className="text-brand flex items-center gap-1.5">
                  <span className="w-3.5 h-3.5 border-2 border-brand border-t-transparent rounded-full animate-spin" />
                  Uploading
                </span>
              )}
              {item.status === 'done' && (
                <span className="text-success-fg">{item.detail || 'Done'}</span>
              )}
              {item.status === 'error' && (
                <span className="text-danger-fg truncate max-w-[160px] sm:max-w-[260px]" title={item.detail}>{item.detail}</span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Upload History */}
      {uploads.length > 0 && (
        <div className="mt-8">
          <h3 className="text-lg font-semibold text-ink mb-4">Upload History</h3>
          <div className="space-y-3">
            {uploads.map(u => (
              <div key={u.id} className="bg-surface-secondary border border-edge-subtle rounded-lg p-3 sm:p-4 flex flex-col sm:flex-row gap-2 sm:gap-3 sm:items-center sm:justify-between">
                <div>
                  <div className="text-sm text-ink font-medium break-words">{u.filename}</div>
                  <div className="text-xs text-ink-subtle mt-0.5">
                    {TYPE_LABELS[u.file_type] || u.file_type} &middot; {new Date(u.created_at).toLocaleDateString()}
                    {u.records_imported > 0 && ` · ${u.records_imported} records`}
                  </div>
                </div>
                <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                  u.status === 'completed' ? 'bg-success/50 text-success-fg' :
                  u.status === 'failed' ? 'bg-danger/40 text-danger-fg' :
                  u.status === 'processing' ? 'bg-info/50 text-info-fg' :
                  'bg-surface-tertiary text-ink-muted'
                }`}>
                  {u.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
